import React, { useState, useEffect } from "react";
import { InputBase, IconButton, Paper, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MicIcon from "@mui/icons-material/Mic";
import { MAIN_COLOR_RED } from "../../assets/styles";

interface RoundedSearchBarProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}

const RoundedSearchBar: React.FC<RoundedSearchBarProps> = ({
                                                               value,
                                                               onChange,
                                                               placeholder = "Procurar",
                                                           }) => {
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);

    useEffect(() => {
        // Check if browser supports speech recognition
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognitionInstance = new SpeechRecognition();
            
            recognitionInstance.continuous = false;
            recognitionInstance.interimResults = false;
            recognitionInstance.lang = 'en-US'; // Can be changed to 'pt-BR' for Portuguese
            recognitionInstance.maxAlternatives = 1;
            
            // Add timeout to prevent hanging
            let recognitionTimeout: NodeJS.Timeout;
            
            recognitionInstance.onstart = () => {
                setIsListening(true);
                // Set a timeout to stop recognition if it takes too long
                recognitionTimeout = setTimeout(() => {
                    if (isListening) {
                        recognitionInstance.stop();
                        alert('Voice recognition timed out. Please try again.');
                    }
                }, 10000); // 10 second timeout
            };
            
            recognitionInstance.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                onChange(transcript);
                setIsListening(false);
            };
            
            recognitionInstance.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
                
                // Provide user-friendly error messages
                let errorMessage: string;
                switch (event.error) {
                    case 'network':
                        errorMessage = 'Network error. Please check your internet connection and try again.';
                        break;
                    case 'not-allowed':
                        errorMessage = 'Microphone access denied. Please allow microphone permissions.';
                        break;
                    case 'no-speech':
                        errorMessage = 'No speech detected. Please try speaking again.';
                        break;
                    case 'audio-capture':
                        errorMessage = 'Audio capture failed. Please check your microphone.';
                        break;
                    case 'service-not-allowed':
                        errorMessage = 'Speech recognition service not available.';
                        break;
                    default:
                        errorMessage = `Voice recognition error: ${event.error}. Please try again.`;
                }
                
                // Show error message to user (you can replace this with a toast notification)
                if (event.error !== 'no-speech') { // Don't show error for no-speech as it's common
                    alert(errorMessage);
                }
            };
            
            recognitionInstance.onend = () => {
                setIsListening(false);
                // Clear the timeout
                if (recognitionTimeout) {
                    clearTimeout(recognitionTimeout);
                }
            };
            
            setRecognition(recognitionInstance);
        }
    }, [isListening, onChange]);

    const handleMicClick = () => {
        if (!recognition) {
            alert('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
            return;
        }

        if (isListening) {
            recognition.stop();
        } else {
            try {
                // Check if we're on HTTPS (required for microphone access)
                if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
                    alert('Voice recognition requires HTTPS. Please use a secure connection.');
                    return;
                }
                
                recognition.start();
            } catch (error) {
                console.error('Failed to start speech recognition:', error);
                alert('Failed to start voice recognition. Please try again.');
            }
        }
    };

    return (
        <Paper
            component="form"
            elevation={2}
            sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: "999px",
                px: 1.5,
                py: 0.5,
                width: "100%",
            }}
        >
            <SearchIcon sx={{ color: "gray", mr: 1 }} />
            <InputBase
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                sx={{ flex: 1 }}
            />
            <Tooltip title={isListening ? "Stop listening" : "Start voice search"}>
                <IconButton 
                    onClick={handleMicClick}
                    sx={{
                        color: isListening ? MAIN_COLOR_RED : "gray",
                        animation: isListening ? "pulse 1.5s infinite" : "none",
                        "@keyframes pulse": {
                            "0%": {
                                transform: "scale(1)",
                                opacity: 1
                            },
                            "50%": {
                                transform: "scale(1.1)",
                                opacity: 0.7
                            },
                            "100%": {
                                transform: "scale(1)",
                                opacity: 1
                            }
                        }
                    }}
                >
                    <MicIcon />
                </IconButton>
            </Tooltip>
        </Paper>
    );
};

export default RoundedSearchBar;
